import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatINR, fmtDate } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Waves, Loader2, ReceiptText, Wallet } from "lucide-react";

export default function SewerageDetail() {
  const { cid } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(1);
  const [generating, setGenerating] = useState(false);
  const officer = user && ["data_entry_operator", "revenue_officer", "executive_officer", "super_admin", "municipal_commissioner", "state_admin"].includes(user.role);

  const load = () => {
    api.get(`/sewerage/consumers/${cid}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [cid]);

  const gen = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/sewerage/generate-bill", { connection_id: data.consumer.id, period_months: Number(months) });
      toast.success(`Bill generated: ${formatINR(r.data.bill_amount)}`);
      load();
    } catch (err) {
      toast.error("Failed to generate bill", { description: err.response?.data?.detail });
    } finally { setGenerating(false); }
  };

  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const c = data.consumer;
  const demands = data.demands || [];

  return (
    <div className="space-y-6" data-testid="sewerage-detail-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Sewerage Connection</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
          <Waves className="w-6 h-6 text-[#0D7650]" /> {c.consumer_name}
        </h1>
        <div className="text-xs font-mono text-slate-500 mt-1">{c.connection_id}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2"><CardContent className="p-6 grid grid-cols-2 gap-4 text-sm">
          <div><div className="text-xs text-slate-500">Address</div><div>{c.address}</div></div>
          <div><div className="text-xs text-slate-500">Ward</div><div>{c.ward}</div></div>
          <div><div className="text-xs text-slate-500">Connection Type</div><Badge variant="outline" className="capitalize">{c.connection_type}</Badge></div>
          <div><div className="text-xs text-slate-500">Size</div><div>{c.connection_size_mm} mm</div></div>
          <div><div className="text-xs text-slate-500">Seats / WCs</div><div>{c.number_of_seats}</div></div>
          <div><div className="text-xs text-slate-500">Built-up Area</div><div>{c.built_up_area} sqft</div></div>
          <div><div className="text-xs text-slate-500">Monthly Charge</div><div className="font-semibold">{formatINR(c.monthly_charge)}</div></div>
          <div><div className="text-xs text-slate-500">Annual Charge</div><div className="font-semibold">{formatINR(c.annual_charge)}</div></div>
        </CardContent></Card>

        <Card className="bg-[#0F2942] text-white"><CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest opacity-70">Outstanding Dues</div>
          <div className="text-3xl font-heading font-bold mt-2">{formatINR(c.total_dues)}</div>
          <div className="text-xs opacity-80 mt-3">Consumer: {c.consumer_email || "—"}</div>
        </CardContent></Card>
      </div>

      {officer && (
        <Card><CardContent className="p-6">
          <div className="text-sm font-semibold mb-3">Generate Bill</div>
          <div className="flex items-end gap-3 flex-wrap">
            <div><Label>Period (months)</Label><Input type="number" value={months} min={1} max={12} onChange={(e) => setMonths(e.target.value)} className="w-32" data-testid="input-bill-months" /></div>
            <Button onClick={gen} disabled={generating} className="bg-[#0D7650] hover:bg-[#095a3d] text-white" data-testid="btn-gen-sewer-bill">
              <ReceiptText className="w-4 h-4 mr-2" /> {generating ? "Generating..." : "Generate Bill"}
            </Button>
          </div>
        </CardContent></Card>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800 text-sm font-semibold">Demand History</div>
          <table className="w-full text-sm" data-testid="sewer-demands-table">
            <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-2 px-4">Demand ID</th>
                <th className="text-left py-2 px-4">Period</th>
                <th className="text-right py-2 px-4">Amount</th>
                <th className="text-left py-2 px-4">Status</th>
                <th className="text-left py-2 px-4">Due Date</th>
                <th className="text-right py-2 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {demands.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">No demands yet.</td></tr>}
              {demands.map((d) => (
                <tr key={d.id} className="border-t border-slate-200 dark:border-slate-800">
                  <td className="py-2 px-4 font-mono text-xs">{d.demand_id}</td>
                  <td className="py-2 px-4">{d.period}</td>
                  <td className="py-2 px-4 text-right font-mono">{formatINR(d.total)}</td>
                  <td className="py-2 px-4"><Badge variant={d.status === "paid" ? "default" : d.status === "pending" ? "outline" : "destructive"} className="capitalize">{d.status}</Badge></td>
                  <td className="py-2 px-4">{fmtDate(d.due_date)}</td>
                  <td className="py-2 px-4 text-right">
                    {d.status !== "paid" && <Link to={`/payments?demand=${d.id}`}><Button size="sm" variant="outline" className="gap-1"><Wallet className="w-3.5 h-3.5" /> Pay</Button></Link>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
